import type { CityConfig } from "../city-template/types";

export const fernleyConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fernley",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "fernley",
  route: "/cities/fernley",
  title: "Emergency Passport Fernley NV | Expedited Passport Renewal Lyon County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Fernley, NV — as fast as 24 hours. Fast passport services for Fernley, Lyon County, and the I-80 corridor travelers. A+ BBB rated, registered State Dept. courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Fernley, NV",
  heroSubtitle: "Passport expired before your trip from the I-80 corridor? Fast Passport Center helps Fernley, Silver Springs, and Lyon County travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada has no local passport agency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fernley%20Nevada%20aerial%20view%20of%20the%20I-80%20corridor%20town%20with%20Truckee%20Canal%20water%20feature%2C%20high%20desert%20landscape%20with%20sagebrush%20and%20mountains%20stretching%20to%20the%20horizon%2C%20semi-rural%20Nevada%20town%20atmosphere%20with%20clean%20streets%20and%20open%20sky%2C%20agricultural%20fields%20visible%20near%20the%20canal%2C%20wide%20panoramic%20desert%20town%20photography%2C%20distant%20mountain%20range%20silhouettes%20with%20dramatic%20evening%20sky%2C%20Reno%20Sparks%20area%20visible%20faintly%20in%20the%20far%20background%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=fernley-nv-hero-001&orientation=landscape",
  heroImageAlt: "Fernley Nevada aerial view of the I-80 corridor town with Truckee Canal and high desert landscape",
  geoPosition: "39.6080;-119.2518",
  geoPlacename: "Fernley, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St, Suite 600, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 250 miles from Fernley via I-80",
  },
  nearbyCitiesZips: [
    { name: "Fernley", zips: "89408" },
    { name: "Silver Springs", zips: "89429" },
    { name: "Wadsworth", zips: "89442" },
    { name: "Fallon", zips: "89406" },
    { name: "Reno", zips: "89501, 89502, 89503" },
    { name: "Sparks", zips: "89431, 89434, 89436" },
    { name: "Dayton", zips: "89403" },
    { name: "Yerington", zips: "89447" },
    { name: "Lovelock", zips: "89419" },
    { name: "Carson City", zips: "89701" },
    { name: "Winnemucca", zips: "89445" },
    { name: "Verdi", zips: "89439" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Fernley and I-80 corridor departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Fernley or Reno" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Reno", route: "/cities/reno" },
    { name: "Fast passport renewal in Sparks", route: "/cities/sparks" },
    { name: "Expedited passport help in Carson City", route: "/cities/carson-city" },
    { name: "Urgent passport services in Sacramento", route: "/cities/sacramento" },
    { name: "Same-day passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Passport courier services in Northern Nevada", route: "/states/nevada" },
  ],
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  fpcOfficeNote: "Fast Passport Center serves Fernley remotely via secure overnight courier. We do not have a walk-in office in Fernley — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Fernley, Silver Springs, and rural Lyon County residents.",
  fpcOfficeDistance: "Serving all of Fernley, Silver Springs, Wadsworth, and the I-80 corridor",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Fernley and Lyon County. As the region's fastest-growing community — driven by the industrial logistics corridor near the Tahoe-Reno Industrial Center — Fernley has seen major growth with Amazon, Walmart Distribution, and other logistics giants. We serve this rapidly expanding community with fast passport processing.",
  nearbyCitiesIntro: "We serve all Fernley neighborhoods and nearby communities including Silver Springs, Wadsworth, Fallon, Dayton, Yerington, Lovelock, and the entire I-80 corridor between Reno and Winnemucca. Our overnight shipping labels work from any UPS location — your rural Nevada location doesn't slow us down.",
  howItWorksIntro: "Most Fernley travelers complete everything online and ship documents overnight. No need to drive to Reno, Sacramento, or San Francisco. Our emergency passport process delivers fast results for I-80 corridor travelers.",
  mistakesIntro: "Fernley and Lyon County residents face longer travel distances to government services than most. Here are the most common passport mistakes we see in the I-80 corridor.",
  testimonialsIntro: "Fernley and Lyon County travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fernley? Here are the most common ones we hear from Lyon County and I-80 corridor travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (250 miles via I-80)",
        "Donner Pass can add hours of travel",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Fernley travelers with immediate departure needs — as fast as 24 hours for urgent flights from Reno-Tahoe International Airport.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance for Fernley and Lyon County residents.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance — especially helpful for logistics industry workers who travel frequently.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Fernley Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Fernley NV and Lyon County." },
    { name: "New Passport", description: "First-time passport application assistance for Fernley and Lyon County residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Mike H.",
      location: "Fernley, NV",
      rating: 5,
      text: "Amazon logistics manager — found out my passport expired 3 days before a trip to Germany. Fast Passport Center got it renewed in 36 hours. I made my flight and didn't miss the international operations meeting.",
      service: "Emergency Passport",
    },
    {
      name: "Sandra W.",
      location: "Silver Springs, NV",
      rating: 5,
      text: "Family trip to the Bahamas and my passport was expired. Even from Silver Springs, the overnight shipping worked perfectly. New passport in 3 days. Amazing.",
      service: "Passport Renewal",
    },
    {
      name: "Carlos R.",
      location: "Fernley, NV",
      rating: 5,
      text: "Walmart Distribution employee traveling to China for training. Passport expired, needed it fast. They delivered in 48 hours. Couldn't ask for better service.",
      service: "Emergency Passport",
    },
    {
      name: "Janet M.",
      location: "Wadsworth, NV",
      rating: 5,
      text: "Living in rural Wadsworth, I was worried about passport processing speed. Fast Passport Center proved me wrong — 4 days from Fernley to my door. The overnight shipping system works perfectly even out here.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "fernley-1",
      name: "Fernley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "440 E Main St",
      city: "Fernley",
      state: "NV",
      zip: "89408",
      phone: "(775) 575-2421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Fernley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Fernley facility. Limited hours — call ahead to confirm passport acceptance times. Appointments recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "fernley-2",
      name: "Fallon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1150 W Williams Ave",
      city: "Fallon",
      state: "NV",
      zip: "89406",
      phone: "(775) 423-4675",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles east of Fernley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Fallon and Churchill County. Larger facility with more appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "fernley-3",
      name: "Sparks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1075 Baring Blvd",
      city: "Sparks",
      state: "NV",
      zip: "89434",
      phone: "(775) 355-4405",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles west of Fernley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Nearest large facility in the Reno-Sparks metro. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "fernley-4",
      name: "Lyon County Clerk — Yerington",
      type: "County Clerk Passport Acceptance",
      address: "27 S Main St",
      city: "Yerington",
      state: "NV",
      zip: "89447",
      phone: "(775) 463-6501",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "35 miles south of Fernley",
      website: "https://www.lyon-county.org/",
      websiteLabel: "Lyon County Clerk",
      notes: "County government passport acceptance. Serves all of Lyon County including Fernley. Call for appointment.",
      icon: "ri-government-line",
    },
    {
      id: "fernley-5",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles west of Fernley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest passport facility in Northern Nevada. Full services with appointment. 30 miles from Fernley via I-80.",
      icon: "ri-mail-line",
    },
    {
      id: "fernley-6",
      name: "Silver Springs Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1150 US Highway 95A N",
      city: "Silver Springs",
      state: "NV",
      zip: "89429",
      phone: "(775) 577-2431",
      hours: "Mon–Fri: 9:00 AM – 1:00 PM, 2:00 PM – 4:00 PM",
      distance: "15 miles southeast of Fernley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small facility with limited hours. Convenient for Silver Springs and Stagecoach residents. Call ahead.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 250 miles to San Francisco over Donner", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Fernley has limited photo options. Passport photos must be exactly 2×2 inches with a white or off-white background — don't settle for a generic drugstore photo setup.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at rural Nevada acceptance facilities like the Fernley Post Office.",
    },
    {
      icon: "ri-cloud-windy-line",
      title: "Underestimating Winter Mountain Travel",
      description: "Driving to the San Francisco Passport Agency requires crossing the Sierra Nevada over Donner Pass — which can be extremely dangerous in winter. Our courier service eliminates this risk entirely.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments — and you'll have driven 250 miles for nothing.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Fernley families who try to handle the process alone at the small post office.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency. Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel, especially from Fernley where mail service is slower.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Fernley or Fallon." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fernley address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Fernley, NV?",
      a: "Fast Passport Center offers emergency passport processing in Fernley in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest is San Francisco, 250 miles via I-80 over Donner Pass — so our courier service is by far the fastest and safest option for Fernley and Lyon County travelers.",
    },
    {
      q: "What is the nearest passport agency to Fernley?",
      a: "The nearest passport agency to Fernley is the San Francisco Passport Agency, approximately 250 miles west via I-80 — over the Sierra Nevada and Donner Pass. In winter, this drive can easily take 5+ hours with mountain conditions. Fast Passport Center's 24–48 hour courier service means you never have to make that dangerous drive.",
    },
    {
      q: "Does Fernley have a passport acceptance facility?",
      a: "Yes, the Fernley Post Office at 440 E Main St offers passport acceptance services by appointment. However, hours are limited (Mon–Fri 9AM–4PM) and the facility is small. Larger options include the Fallon Post Office 15 miles east, the Sparks Post Office 25 miles west, and the Reno Main Post Office 30 miles west for the most comprehensive service.",
    },
    {
      q: "Do you serve the Tahoe-Reno Industrial Center workers in Fernley?",
      a: "Absolutely. The Tahoe-Reno Industrial Center — the largest industrial park in the world — spans the area between Fernley and Sparks. Home to Amazon, Walmart Distribution, Petco, and many logistics companies. Fast Passport Center serves all workers and families in the Fernley and TRI corridor with fast passport processing.",
    },
    {
      q: "What if I work in logistics and need a passport for international shipping travel?",
      a: "Fernley's location on the I-80 corridor and proximity to the Tahoe-Reno Industrial Center means many residents work in logistics, supply chain, and warehousing with international travel needs. Fast Passport Center provides fast renewal and emergency passport services tailored to logistics professionals who need quick turnaround for work travel.",
    },
    {
      q: "Can I use the Reno-Tahoe Airport from Fernley?",
      a: "Yes. Reno-Tahoe International Airport is only about 30 miles west of Fernley via I-80 — about a 30-minute drive. Direct international flights to Mexico and connecting flights worldwide make it convenient for Fernley travelers, and Fast Passport Center ensures your passport is ready before departure.",
    },
    {
      q: "How much does expedited passport service cost in Fernley?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your departure date and processing speed.",
    },
  ],
  photoLocations: [
    { name: "Fernley Post Office", address: "440 E Main St, Fernley, NV 89408", hours: "Mon–Fri: 9AM–4PM (photo by appointment)", website: "https://tools.usps.com/find-location.htm" },
    { name: "Walgreens — Sparks", address: "1075 Baring Blvd, Sparks, NV 89434", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator" },
    { name: "Walmart — Fernley", address: "1550 E Newlands Dr, Fernley, NV 89408", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "UPS Store — Sparks", address: "1845 E Prater Way, Sparks, NV 89434", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "CVS Pharmacy — Fallon", address: "2160 W Williams Ave, Fallon, NV 89406", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walmart — Fallon", address: "2333 Reno Hwy, Fallon, NV 89406", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "FedEx Office — Reno (near Sparks)", address: "1201 Terminal Way, Reno, NV 89502", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/en-us/location.html" },
    { name: "Costco — Sparks", address: "989 Sparks Blvd, Sparks, NV 89434", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/warehouse-locations" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Reno",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Primary Northern Nevada DMV. 60 miles from Fernley. REAL ID processing. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Nevada DMV — Sparks",
      address: "1775 Hymer Ave, Sparks, NV 89431",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Closest DMV to Fernley — about 25 miles via I-80. Full-service with REAL ID.",
      featured: true,
    },
    {
      name: "Lyon County Clerk — Yerington",
      address: "27 S Main St, Yerington, NV 89447",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government services for Lyon County residents. Passport acceptance available.",
      featured: false,
    },
    {
      name: "Nevada DMV — Fallon Express",
      address: "111 Industrial Way, Fallon, NV 89406",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Smaller DMV express office in Fallon. Limited services — call ahead for passport-related documentation.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "30 miles from Fernley via I-80",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "The primary airport for Fernley and Northern Nevada. Direct service to Mexico and connecting international flights. 30-mile drive via I-80.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "Reno-Tahoe Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "250 miles from Fernley via I-80",
      address: "San Francisco, CA 94128",
      note: "Primary West Coast hub. Home to the San Francisco Passport Agency. Requires crossing the Sierra Nevada over Donner Pass.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "160 miles from Fernley via I-80",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative regional hub accessible via I-80. Some Fernley travelers prefer this over Reno for certain international connections.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};